import toast from '@liquidweb-dev/undercurrent/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import {
	type LWApiPublicNetworkDnsDomainAutoRenewParamsRaw,
	type LWApiPublicNetworkDnsDomainAutoRenewResultRaw,
} from '@liquidweb-dev/club-sauce/public/network/dns/domain/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import type { LWApiPublicDomainListResultRawI } from '@liquidweb-dev/club-sauce/public/domain/index.raw';
import api from 'modules/queries/api';
import { DOMAIN_LIST_QUERY_KEY } from 'modules/queries/domain/useList';

type PreviousQueriesContext = Array<
	[QueryKey, LWApiPublicDomainListResultRawI | undefined]
>;

interface MutationContext {
	previousQueries: PreviousQueriesContext;
}

const useDomainAutoRenew = () => {
	const queryClient = useQueryClient();
	return useMutation<
		LWApiPublicNetworkDnsDomainAutoRenewResultRaw,
		ApiErrorI,
		LWApiPublicNetworkDnsDomainAutoRenewParamsRaw,
		MutationContext
	>({
		mutationFn: (params) => api.network.dns.domain.apiRaw.autoRenew(params),

		onMutate: async (params) => {
			// Cancel any outgoing refetches to avoid overwriting our optimistic update
			await queryClient.cancelQueries({
				queryKey: [DOMAIN_LIST_QUERY_KEY],
			});

			// Snapshot the previous value for rollback
			const previousQueries: PreviousQueriesContext =
				queryClient.getQueriesData<LWApiPublicDomainListResultRawI>({
					queryKey: [DOMAIN_LIST_QUERY_KEY],
				});

			// Optimistically update all matching queries
			queryClient.setQueriesData<LWApiPublicDomainListResultRawI>(
				{ queryKey: [DOMAIN_LIST_QUERY_KEY] },
				(oldData) => {
					if (!oldData?.items) {
						return oldData;
					}

					return {
						...oldData,
						items: oldData.items.map((item) => {
							if (item.dreg?.uniq_id === params.uniq_id && item.dreg) {
								return {
									...item,
									dreg: {
										...item.dreg,
										autorenew: params.allow,
									} satisfies typeof item.dreg,
								};
							}
							return item;
						}),
					};
				},
			);

			// Return context with previous data for rollback
			return { previousQueries };
		},

		onError: (_error, _params, context) => {
			// Rollback to previous data on error
			if (context?.previousQueries) {
				context.previousQueries.forEach(
					([queryKey, data]: [
						QueryKey,
						LWApiPublicDomainListResultRawI | undefined,
					]) => {
						queryClient.setQueryData(queryKey, data);
					},
				);
			}
		},

		onSuccess: (_data, params) => {
			const isEnabled = fromApiBoolean(params.allow);
			toast.success(
				isEnabled
					? 'Auto-renew has been enabled'
					: 'Auto-renew has been disabled',
			);

			// Invalidate to ensure we have the latest data from the server
			queryClient.invalidateQueries({
				queryKey: [DOMAIN_LIST_QUERY_KEY],
			});
		},
	});
};
export default useDomainAutoRenew;
